import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_URL } from '../utils/apiConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const loggingOutRef = React.useRef(false); // Flag to prevent race conditions during logout
  const tokenRef = React.useRef(null); // Ref to track token in interval callbacks (avoids stale closures)

  // Keep tokenRef in sync with token state
  React.useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    const initAuth = async () => {
      console.log('[AUTH INIT] Starting authentication initialization...');

      // Safe localStorage wrapper — Safari private mode throws on localStorage access
      const safeGet = (key) => {
        try { return localStorage.getItem(key); } catch (e) { return null; }
      };
      const safeSet = (key, value) => {
        try { localStorage.setItem(key, value); } catch (e) { }
      };
      const safeRemove = (key) => {
        try { localStorage.removeItem(key); } catch (e) { }
      };

      // 1. Load any existing auth state from localStorage
      let storedToken = safeGet('token');

      console.log('[AUTH INIT] Stored token:', storedToken ? 'EXISTS' : 'NULL');

      // 2. Always check for a `token` in the URL (e.g. after redirect from main domain)
      const searchParams = new URLSearchParams(window.location.search);
      const urlToken = searchParams.get('token');

      if (urlToken) {
        console.log('[AUTH INIT] URL token: FOUND');
        storedToken = urlToken;
        safeSet('token', urlToken);
        console.log('[AUTH INIT] ✅ Token saved from URL to localStorage');

        // Clean token from the URL for security / neatness
        searchParams.delete('token');
        const newQuery = searchParams.toString();
        const newUrl = `${window.location.pathname}${newQuery ? `?${newQuery}` : ''}${window.location.hash}`;
        window.history.replaceState({}, '', newUrl);
        console.log('[AUTH INIT] ✅ Token removed from URL');
      } else {
        console.log('[AUTH INIT] URL token: NOT FOUND');
      }

      // 3. Wake up backend if it's sleeping (Render cold start)
      try {
        fetch(`${API_URL}/api/health`, { mode: 'no-cors' }).catch(() => { });
      } catch (e) { }

      if (storedToken) {
        setToken(storedToken);

        // ── FAST PATH ──────────────────────────────────────────────────────────
        // If we have a cached user, load it immediately and render the app NOW.
        // Token validation runs in the background so the app is never blocked.
        const cachedUserRaw = safeGet('user');
        if (cachedUserRaw) {
          try {
            const cachedUser = JSON.parse(cachedUserRaw);
            setCurrentUser(cachedUser);
            console.log('[AUTH INIT] ✅ Cached user loaded instantly — app ready');
          } catch (e) {
            // Corrupt cache — fall through to blocking validation below
          }
        }

        // Always mark loading done immediately if we have something in cache.
        // Background validation will silently update or log out if needed.
        if (cachedUserRaw) {
          setIsLoading(false);
          console.log('[AUTH INIT] ✅ Authentication initialization complete (instant)');
        }

        // ── BACKGROUND VALIDATION ──────────────────────────────────────────────
        // Validate the token against the backend with a hard 6-second timeout.
        // This runs AFTER the app is already visible, so it never blocks render.
        const validateInBackground = async () => {
          try {
            console.log('[AUTH INIT] 🔄 Background token validation starting...');
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 6000); // 6s max

            const response = await fetch(`${API_URL}/api/auth/me`, {
              headers: { 'Authorization': `Bearer ${storedToken}` },
              signal: controller.signal
            });
            clearTimeout(timeoutId);

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.user) {
                const userData = {
                  ...data.user,
                  permissions: data.user.permissions || null
                };
                safeSet('user', JSON.stringify(userData));
                setCurrentUser(userData);
                console.log('[AUTH INIT] ✅ Background validation: token valid, user refreshed');
              } else {
                throw new Error('Invalid response data');
              }
            } else if (response.status === 401 || response.status === 403) {
              // Server explicitly rejected the token — must log out
              console.warn('[AUTH INIT] ⚠️ Background validation: token rejected (401/403) — clearing session');
              safeRemove('token');
              safeRemove('user');
              setToken(null);
              setCurrentUser(null);
            }
            // Any other HTTP error (500, etc.) — keep cached session, server issue
          } catch (err) {
            if (err && err.name === 'AbortError') {
              console.warn('[AUTH INIT] ⚠️ Background validation timed out (6s) — keeping cached session');
            } else {
              console.warn('[AUTH INIT] ⚠️ Background validation failed (offline?) — keeping cached session', err);
            }
            // Keep cached session — do not log out on network errors
          } finally {
            // If we had no cached user, we waited for this to finish — mark done now
            if (!cachedUserRaw) {
              setIsLoading(false);
              console.log('[AUTH INIT] ✅ Authentication initialization complete (no cache)');
            }
          }
        };

        if (cachedUserRaw) {
          // App already rendered — run validation silently in background
          validateInBackground();
        } else {
          // No cached user — must wait for server response before showing the app
          await validateInBackground();
        }

      } else {
        console.log('[AUTH INIT] No token available - user not authenticated');
        setIsLoading(false);
        console.log('[AUTH INIT] ✅ Authentication initialization complete');
      }
    };

    initAuth();

    // Periodic check: every 5 seconds, verify token still exists in storage.
    // Also enforces 24-hr inactivity logout for admin users.
    const INACTIVITY_LIMIT_MS = 24 * 60 * 60 * 1000; // 24 hours
    const tokenCheckInterval = setInterval(() => {
      if (loggingOutRef.current) return; // Skip check during logout
      try {
        const currentToken = localStorage.getItem('token');
        // Only redirect if we HAD a token in state (logged in) but storage is now empty
        // We reference the ref-captured token to avoid stale closures
        if (!currentToken && tokenRef.current) {
          console.log('[AUTH] 🔄 Token cleared on interval - redirecting to login');
          clearInterval(tokenCheckInterval);
          const pathname = window.location.pathname;
          const lastSlashIdx = pathname.lastIndexOf('/');
          const dir = lastSlashIdx > 0 ? pathname.substring(0, lastSlashIdx) : '';
          window.location.href = `${dir}/signup?mode=login`;
          return;
        }
        // 24-hr inactivity check (only when logged in)
        if (currentToken && tokenRef.current) {
          const lastActivity = parseInt(localStorage.getItem('auth_last_activity') || '0', 10);
          if (lastActivity > 0 && (Date.now() - lastActivity) > INACTIVITY_LIMIT_MS) {
            console.log('[AUTH] ⏰ 24-hr inactivity detected - logging out admin');
            clearInterval(tokenCheckInterval);
            // Clear storage and redirect
            try { localStorage.removeItem('token'); } catch (e) {}
            try { localStorage.removeItem('user'); } catch (e) {}
            try { localStorage.removeItem('auth_last_activity'); } catch (e) {}
            const pathname = window.location.pathname;
            const lastSlashIdx = pathname.lastIndexOf('/');
            const dir = lastSlashIdx > 0 ? pathname.substring(0, lastSlashIdx) : '';
            window.location.href = `${dir}/signup?mode=login`;
          }
        }
      } catch (e) {
        // Safari private mode - localStorage unavailable, skip silently
      }
    }, 5000);

    // Track user activity (throttled) to update last-activity timestamp
    let activityThrottle = null;
    const handleActivity = () => {
      if (activityThrottle) return;
      activityThrottle = setTimeout(() => {
        activityThrottle = null;
        try {
          if (localStorage.getItem('token')) {
            localStorage.setItem('auth_last_activity', Date.now().toString());
          }
        } catch (e) {}
      }, 5000); // Update at most every 5 seconds
    };
    document.addEventListener('mousemove', handleActivity, { passive: true });
    document.addEventListener('keydown', handleActivity, { passive: true });
    document.addEventListener('touchstart', handleActivity, { passive: true });
    document.addEventListener('click', handleActivity, { passive: true });

    // Also check on visibility change (tab switching) — better than click listener
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (loggingOutRef.current) return;
      try {
        const currentToken = localStorage.getItem('token');
        if (!currentToken && tokenRef.current) {
          console.log('[AUTH] 🔄 Token cleared on tab focus - redirecting to login');
          const pathname = window.location.pathname;
          const lastSlashIdx = pathname.lastIndexOf('/');
          const dir = lastSlashIdx > 0 ? pathname.substring(0, lastSlashIdx) : '';
          window.location.href = `${dir}/signup?mode=login`;
        }
      } catch (e) {
        // Safari private mode - localStorage unavailable, skip silently
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(tokenCheckInterval);
      if (activityThrottle) clearTimeout(activityThrottle);
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('keydown', handleActivity);
      document.removeEventListener('touchstart', handleActivity);
      document.removeEventListener('click', handleActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run ONCE on mount only — token changes are tracked via tokenRef below

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (data.success) {
        const authToken = data.data.token;
        const userData = {
          ...data.data,
          permissions: data.data.permissions || null
        };
        try { localStorage.setItem('token', authToken); } catch (e) { }
        try { localStorage.setItem('user', JSON.stringify(userData)); } catch (e) { }
        // Stamp activity time so 24hr inactivity timer starts from login
        try { localStorage.setItem('auth_last_activity', Date.now().toString()); } catch (e) { }
        setToken(authToken);
        setCurrentUser(userData);
        return { success: true, user: userData, company: data.company };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      return { success: false, message: 'An error occurred during login' };
    }
  };

  const signup = async (fullName, email, password, phone, role = 'customer') => {
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fullName, email, password, phone, role })
      });

      const data = await response.json();

      if (data.success) {
        // Auto-login after successful signup
        const authToken = data.data.token;
        const userData = {
          ...data.data,
          permissions: data.data.permissions || null
        };

        console.log('[SIGNUP] Storing auth data:', { token: authToken, user: userData });

        try { localStorage.setItem('token', authToken); } catch (e) { }
        try { localStorage.setItem('user', JSON.stringify(userData)); } catch (e) { }
        // Stamp activity time so 24hr inactivity timer starts from signup
        try { localStorage.setItem('auth_last_activity', Date.now().toString()); } catch (e) { }
        setToken(authToken);
        setCurrentUser(userData);

        return {
          success: true,
          message: 'Registration successful!',
          user: userData,
          company: data.company
        };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('[SIGNUP] Error:', error);
      return { success: false, message: 'An error occurred during registration' };
    }
  };

  const logout = () => {
    console.log('[LOGOUT] Starting logout process...');

    // Set flag FIRST to prevent periodic checks from interfering
    loggingOutRef.current = true;

    // Clear storage and state
    try { localStorage.removeItem('token'); } catch (e) { }
    try { localStorage.removeItem('user'); } catch (e) { }
    try { localStorage.removeItem('auth_last_activity'); } catch (e) { }
    setToken(null);
    setCurrentUser(null);

    // Check if running in Electron
    const isElectron = window.navigator.userAgent.toLowerCase().includes('electron');
    
    // Check if we are on an admin/tenant route (HomePage is a tenant route)
    const path = window.location.pathname;
    const lastSlashIdx = path.lastIndexOf('/');
    const dir = lastSlashIdx > 0 ? path.substring(0, lastSlashIdx) : '';

    if (path.endsWith('/customer.html')) {
      // For customers on the menu page, reload to preserve guest context
      console.log('[LOGOUT] Customer logout - reloading page');
      if (isElectron) {
        window.location.hash = '#/customer.html';
        window.location.reload();
      } else {
        window.location.href = `${window.location.origin}${path}${window.location.search}`;
      }
    } else {
      // For everyone else (admin, staff, tenant HomePage users), go to signup with login mode
      const timestamp = new Date().getTime();
      
      console.log('[LOGOUT] Redirecting to signup');
      if (isElectron) {
        // Use hash navigation and force reload for Electron
        window.location.hash = `#/signup?mode=login&_t=${timestamp}`;
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        const currentOrigin = window.location.origin;
        window.location.href = `${currentOrigin}${dir}/signup?mode=login&_t=${timestamp}`;
      }
    }
  };

  const updateAuthState = (userData) => {
    try { localStorage.setItem('user', JSON.stringify(userData)); } catch (e) { }
    try { localStorage.setItem('token', userData.token); } catch (e) { }
    setToken(userData.token);
    setCurrentUser(userData);
  };

  const value = {
    currentUser,
    token,
    isLoading,
    login,
    signup,
    logout,
    updateAuthState
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;